import { createRootRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';

import { AppShell, useDocumentTitle, VersionBadge, ToastProvider } from '@platform/ui';
import { ObjectLabsMark } from '@platform/ui/object-labs-mark';

import { AccountMenu } from '../auth/account-menu';
import { AuthLayout } from '../auth/components';
import { ImpersonationBannerConnected } from '../lib/impersonation';
import { navigation, pageTitle } from '../lib/nav';
import { SessionExpiryWatcher } from '../lib/session/expiry';
import { MFA_ENROLMENT_PATHS, RequireAuth, RequireMfa } from '../lib/session/guards';
import { usePermissions } from '../lib/session/permissions';
import { StepUpProvider } from '../lib/session/step-up';
import { APP_BUILD, useApiVersion } from '../version';

export const Route = createRootRoute({
  component: RootLayout,
});

function Shell() {
  const api = useApiVersion();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { can } = usePermissions();

  return (
    <AppShell
      nav={navigation(pathname, can)}
      tenantName="HeyAllergy Console"
      /* The super admin app never takes tenant branding (FR-UI-03), so the platform
         logo is used rather than the tenant-overridable one. */
      logoVariant="platform"
      /* Always above the top bar, on every screen, for the whole support session
         (rules §20.5, ADR-005). It draws itself from the session, so it appears and
         disappears with the server's own record and never with local state. */
      banner={<ImpersonationBannerConnected />}
      renderLink={(item, props) => (
        <Link
          to={item.href ?? '/'}
          className={props.className}
          aria-current={props['aria-current']}
        >
          {props.children}
        </Link>
      )}
      userMenu={
        <AccountMenu
          renderLink={({ to, className, children, onClick }) => (
            <Link to={to} className={className} onClick={onClick}>
              {children}
            </Link>
          )}
        />
      }
      footer={
        <>
          <VersionBadge app={APP_BUILD} api={api} />
          {/* The Object Labs mark appears in the super admin footer and nowhere else. */}
          <span className="flex items-center gap-2 text-xs text-neutral-400-text">
            Operated by
            <ObjectLabsMark height={14} />
          </span>
        </>
      }
    >
      <SessionExpiryWatcher />
      <RequireAuth>
        <RequireMfa>
          <Outlet />
        </RequireMfa>
      </RequireAuth>
    </AppShell>
  );
}

/**
 * The console's outermost frame (M2.8).
 *
 * Two layouts, chosen by route rather than by state:
 *
 * - **`/login` and `/mfa/enroll`** render bare, inside `AuthLayout`. A left nav around a
 *   sign-in form is a menu of links the person cannot follow, and a shell implies they
 *   are already inside the console.
 * - **Everything else** renders the `AppShell`, behind `RequireAuth` and `RequireMfa`.
 *   The guards sit *inside* the shell, so the nav, the banner and the account menu stay
 *   on screen while the session is being checked - an operator who refreshes mid-session
 *   sees the console load rather than a flash of an empty page.
 *
 * `StepUpProvider` wraps both: the dialog it renders must outlive any one screen, and a
 * step-up refusal can arrive from a call started on a route that has since changed.
 */
function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const bare = MFA_ENROLMENT_PATHS.includes(pathname);

  /* M13.7 (WCAG 2.4.2): the screen's own name in the tab and in the history. */
  useDocumentTitle(pageTitle(pathname));

  return (
    <ToastProvider>
      <StepUpProvider>
        {bare ? (
          <AuthLayout>
            <Outlet />
          </AuthLayout>
        ) : (
          <Shell />
        )}
      </StepUpProvider>
    </ToastProvider>
  );
}
